import { useNavigate } from 'react-router'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { updateAppAlert } from '../../modules/appAlert/actions'
import { LoadingTypes } from '../../modules/loading/reducer'
import { startLoading, stopLoading } from '../../modules/loading/actions'
import { PartnerType, setAccount, type AccountData } from '../../modules/partner/action'
import { RequestFailureMessage } from '../../utils/messagesContants'
import { TermsAndConditionsComponentUI, CompanyInfo } from './UI'
import { getNormalizedCompanyInfoPartnerUpdate } from './normalizeCompanyInfoPartner'
import { errorLogger, type LoggerGlobalState } from '../../utils/errorLogger'
import { getErrorMessages } from '../../utils/error'
import { AxiosError } from 'axios'
import { actionTypeWrapper } from '../../utils/actionTypeWrapper'
import {
  convertReducerToMicrosoftForm,
  sendFlyOutOnboardingData,
  sendMarketplaceOnboardingData,
} from '../CloudSettings/flyoutOnboardingActions'
import { FlyoutOnboardingSectionName } from '../../types/flyoutOnboarding'
import { CloudTypeEnum } from '../../types'
import { getPageMarkerMapping } from './getPageMarkerMapping'
import { useOnboardingTenantPartnerId } from '../../utils/hooks/useCrmOnboardingContext'
import { useCompanyInfoAccountId } from './useCompanyInfoAccountId'
import { patchPartnerIdentityService } from './patchPartnerIdentityService'
import type { CompanyInfoRootState } from './companyInfoState'
export enum ProductType {
  OPPSYNC = 'OPPSYNC',
  CLOUDFAAS = 'CLOUDFAAS',
  FLYOUT = 'FLYOUT',
  LABRA_AI = 'LABRA_AI',
}
type TermsAndConditionsProps = {
  productType?: ProductType
}
export const TermsAndConditionsComponent: React.FC<TermsAndConditionsProps> = ({
  productType = ProductType.OPPSYNC,
}) => {
  const dispatch = useDispatch() as (action: unknown) => Promise<unknown>
  const store = useStore()
  const navigate = useNavigate()
  const tenantPartnerId = useOnboardingTenantPartnerId()
  const microsoftOnboardingValues = useSelector(
    (state: CompanyInfoRootState) =>
      state.flyOutOnboarding.microsoftOnboardingForm
  )
  const awsOnboardingValues = useSelector(
    (state: CompanyInfoRootState) => state.flyOutOnboarding.onboardingForm
  )
  const accountIdAWS = useCompanyInfoAccountId({
    product: 'flyout',
    cloud: CloudTypeEnum.AWS,
  })
  const accountIdMicrosoft = useCompanyInfoAccountId({
    product: 'flyout',
    cloud: CloudTypeEnum.AZURE,
  })
  const partnerData = useSelector(
    (state: CompanyInfoRootState) =>
      state.AccountData.user.partnerData || undefined
  )
  const subscribedProducts = partnerData?.subscribedProducts
  const flyoutProduct = subscribedProducts?.FLYOUT
  const flyoutSubscribedCloudProviders = flyoutProduct?.cloudProviders || []
  const isMultipleCloudsSubscribed = flyoutSubscribedCloudProviders?.length > 1
  const isAzureCloudSettingsFilled =
    microsoftOnboardingValues?.filledCloudSettings || false
  const isAwsCloudSettingsFilled =
    awsOnboardingValues?.[FlyoutOnboardingSectionName.PAGE_MARKER]?.page2 ||
    false
  const isCloudSettingsNotFilled =
    !isAzureCloudSettingsFilled && !isAwsCloudSettingsFilled
  const { partnerId, companyInfo   } = useSelector((state: CompanyInfoRootState) => {
    const account = state.AccountData[PartnerType.User].partnerData
    return {
      partnerId: account?.partnerId || '',
      companyInfo: {
        companyName: account?.companyName || '',
        accelerateProgram: account?.accelerateProgram ?? false,
        industry: account?.industry || '',
        isIsv: account?.isIsv ?? false,
        websiteURL: account?.websiteUrl || '',
        partnershipTier: account?.partnershipTier || null,
        partnershipType: (account?.partnershipType || '') as string,
        isRevenueRecognitionProgram: account?.isSrrp || false,
        acceptedTermsAndConditions:
          account?.acceptedTermsAndConditions ?? false,
      },
    }
  })
  const sendData = async (values: CompanyInfo) => {
    dispatch(startLoading(LoadingTypes.COMPANY_INFORMATION))
    try {
      const { data: partnerDataIS } = await patchPartnerIdentityService(
        partnerId,
        {
          acceptedTermsAndConditions: values.acceptedTermsAndConditions,
          companyWebsite: values.websiteURL,
          companyName: values.companyName,
          companyIndustry: values.industry,
          partnershipType: values.partnershipType,
        } as Partial<AccountData>
      )
      const updatedPartner = getNormalizedCompanyInfoPartnerUpdate({
        identityPartner: partnerDataIS,
        formValues: values,
        subscribedProducts: partnerData?.subscribedProducts,
      })
      dispatch(actionTypeWrapper(PartnerType.User, setAccount(updatedPartner)))
      if (productType === ProductType.CLOUDFAAS) {
        navigate('/cloudfaas/onboarding/cloud-settings')
      } else if (productType === ProductType.LABRA_AI) {
        navigate('/labra-ai/onboarding/cloud-settings')
      } else if (productType === ProductType.FLYOUT) {
        if (flyoutSubscribedCloudProviders?.includes('AZURE')) {
          let updatedMicrosoftValues = microsoftOnboardingValues
          updatedMicrosoftValues = {
            ...updatedMicrosoftValues,
            filledCompanyInfo: true,
            companyName: updatedPartner.companyName,
            companyIndustry: updatedPartner.industry,
            companyWebsite: updatedPartner.websiteUrl,
            acceptedTermsAndConditions:
              partnerDataIS?.accepted_terms_and_conditions,
            ...(isAzureCloudSettingsFilled && {
              submit: true,
            }),
          }
          const reducerToForm = convertReducerToMicrosoftForm(
            updatedMicrosoftValues
          )
          await dispatch(
            sendMarketplaceOnboardingData(
              accountIdMicrosoft || '',
              reducerToForm,
              CloudTypeEnum.AZURE as never
            )
          )
        }
        if (flyoutSubscribedCloudProviders?.includes('AWS')) {
          let updatedAwsValues = awsOnboardingValues
          updatedAwsValues = {
            ...updatedAwsValues,
            [FlyoutOnboardingSectionName.PAGE_MARKER]: {
              ...updatedAwsValues[FlyoutOnboardingSectionName.PAGE_MARKER],
              page1: true,
            },
            [FlyoutOnboardingSectionName.LABRA_PARTNER_DETAILS]: {
              ...updatedAwsValues[
                FlyoutOnboardingSectionName.LABRA_PARTNER_DETAILS
              ],
              companyName: updatedPartner.companyName,
              industry: updatedPartner.industry,
              companyWebsite: updatedPartner.websiteUrl,
              formFilled: true,
            },
          }
          await dispatch(
            sendFlyOutOnboardingData({
              partnerId: accountIdAWS || '',
              tenantPartnerId: tenantPartnerId ?? '',
              isAutoSave: false,
              onboardingForm: updatedAwsValues,
              pageMarkerMapping: getPageMarkerMapping(),
              page: FlyoutOnboardingSectionName.LABRA_PARTNER_DETAILS,
              status: '',
            })
          )
        }
        if (isCloudSettingsNotFilled) {
          if (isMultipleCloudsSubscribed) {
            await navigate('/flyout/onboarding/cloud-settings/aws')
          } else if (flyoutSubscribedCloudProviders?.includes('AWS')) {
            await navigate('/flyout/onboarding/cloud-settings/aws')
          } else if (flyoutSubscribedCloudProviders?.includes('AZURE')) {
            await navigate('/flyout/onboarding/cloud-settings/microsoft')
          } else {
            await navigate('/flyout/onboarding/cloud-settings')
          }
        }
      } else if (productType === ProductType.OPPSYNC) {
        navigate('/oppsync/onboarding/cloud-settings')
      }
    } catch (e) {
      console.warn('something went wrong while saving company info')
      errorLogger({ globalState: store.getState() as LoggerGlobalState })(
        new Error('something went wrong while saving company info')
      )
      const axiosError = e as AxiosError<ErrorResponse>
      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(
            axiosError?.response
          ),
          messageType: 'ERROR',
        })
      )
      const globalState = store.getState() as LoggerGlobalState
      errorLogger({ globalState })(e as Error)
    } finally {
      dispatch(stopLoading(LoadingTypes.COMPANY_INFORMATION))
    }
  }
  return (
    <>
      <TermsAndConditionsComponentUI
        productType={productType}
        companyInfo={companyInfo}
        onSubmit={sendData}
      />
    </>
  )
}
